export const translations = {
	en: {
		menuLink1: "What is EpilexIA?",
		menuLink2: "Why EpilexIA matters",
		menuLink3: "EpilexIA solution",
		menuLink4: "Inside the platform",
		menuLink5: "Benefits for researchers",
		menuLink6: "Benefits for the pharmaceutical industry",
		menuLink7: "Project Phases",
		menuLink8: "Mobile app",
		menuLink9: "Collaborators",
		menuLink10: "Ethics & privacy",
		menuLink11: "Investors & partners",
		menuLink12: "Contact Us",
		heroTitle: "EpilexIA",
		heroTitle2: " Multimodal Artificial Intelligence for Rare Epilepsies.",
		heroDescription:
			"A next-generation multimodal AI platform designed to analyze real medical records of patients with complex epileptic encephalopathies, integrating machine learning, biomedical NLP, computer vision and clinical knowledge graphs.",
		learnMore: "Learn more",
		collaborationInquiry: "Collaboration inquiry",
		menu: "Menu",
		platform: "Platform",
		clinicalChallenge: "Clinical Challenge",
		heroBtn1: "Learn more",
		heroBtn2: "Collaboration inquiry",

		//platform
		platformSectionTitle: "What is EpilexIA?",
		platformSectionContent: `EpilexIA is a next-generation multimodal AI platform designed to transform how epilepsy is analyzed, diagnosed, and researched. It integrates computer vision, NLP, machine learning, generative AI, agentic AI, and graph databases to unify EEG/MRI findings, clinical notes, biometric data, seizure descriptions, and treatment histories into a single intelligence layer.`,
		platformSectionSubtitle: "EpilexIA is:",
		platformSectionPoints: [
			"A multimodal AI engine capable of processing imaging, text, wearable signals, and structured clinical data.",
			"A graph-powered intelligence network mapping symptoms, biomarkers, genetics, comorbidities, medications, and disease trajectories.",
			"A real-world evidence platform built from deeply anonymized medical histories.",
			"An agentic AI system capable of reasoning over patient records, medical literature, drug evidence, and ML-derived insights.",
			"A human-in-the-loop clinical decision-support ecosystem, where expert neurologists guide, validate, and supervise AI reasoning.",
			"A rare-epilepsy research framework specializing in Dravet, West, and Lennox-Gastaut syndromes.",
			"A privacy-first, GDPR-aligned environment co-developed with leading clinicians and foundations.",
		],

		//clinical challenge
		clinicalSectionTitle: "Why EpilexIA matters",
		clinicalSectionSubtitle: "EpilexIA addresses long-standing obstacles in epilepsy care and research:",
		clinicalSectionContent: "EpilexIA brings clarity, integration, and scientific acceleration across the entire field.",
		clinicalSectionPoints: [
			"Rare epilepsies are severe, complex neurological disorders that mainly affect children.",
			"Fragmented clinical information across imaging, narratives, and treatment records.",
			"Limited structured datasets for rare epileptic encephalopathies.",
			"Diagnostic uncertainty caused by variable presentations.",
			"Challenges predicting disease evolution and treatment response.",
			"Manual, slow, labor-intensive research processes.",
			"Need for trustworthy, explainable AI aligned with medical standards.",
		],

		//solution
		solutionSectionTitle: "How EpilexIA works",
		solutionSectionContent: `processes multimodal clinical information through a structured, AI-driven pipeline:`,
		solutionSectionSubTitle1: "Generative AI ETL Layer (Extract Transform Load)",
		solutionSectionSubTitle2: "Multimodal AI Analysis",
		solutionSectionSubTitle3: "Agentic AI for Deep Clinical Reasoning (Human in the Loop)",
		solutionSectionSubTitle4: "Real World Evidence(RWE) Insights & Knowledge Integration",
		solutionSectionSubtitleText1: "EpilexIA incorporates an advanced agentic AI system with medical oversight:",
		solutionSectionSubtitleText2: "This hybrid intelligence model combines AI precision with expert medical judgment.",
		solutionSectionPoints1: [
			"Reads unstructured documents (PDFs, physician notes, EEG/MRI reports).",
			"Extracts key medical concepts, events, timelines, and correlations.",
			"Produces standardized relational datasets for structured analysis.",
			"Automatically builds graph databases capturing relationships across symptoms, imaging, genetics, medications, and outcomes.",
		],
		solutionSectionPoints2: [
			"Computer Vision interprets EEGs, MRIs, and imaging sequences.",
			"NLP structures long-form medical histories.",
			"Machine Learning & Generative AI detect clusters, anomalies, therapeutic patterns, and predictive signals.",
		],
		solutionSectionPoints3: [
			"Cross-references each patient’s data with an expert-curated epilepsy knowledge base.",
			"Reviews correlations with drug mechanisms, side-effect profiles, dosing guidance, and scientific evidence.",
			"Integrates ML insights, temporal patterns, and population-level trends.",
			"Produces structured, explainable reasoning pathways to support clinical understanding.",
			"Expert neurologists remain in the loop, guiding model development, validating interpretations, and ensuring clinical safety.",
		],
		solutionSectionPoints4: [
			"Outputs fuel dashboards, exploratory research tools, and clinical insight modules.",
			"Continuous learning loops improve accuracy as more cases enter the platform.",
			"All processes comply with GDPR and ethical requirements.",
		],

		cliniciansSectionTitle: "Benefits for Clinicians ",
		clicniciandSectionContent: [
			"Fast access to structured patient information.",
			"Improved longitudinal understanding of clinical evolution.",
			"Clear visualization of relevant correlations.",
			"Decision-support for complex clinical scenarios.",
		],
		reasearchersSectionTitle: "Benefits for Researchers",
		researchersSectionContent: [
			"Dynamic, filterable cohorts.",
			"Identification of patterns and potential biomarkers.",
			"Reproducible and traceable analysis.",
			"Collaborative scientific discovery.",
		],
		pharmaSectionTitle: "Benefits for the Pharmaceutical Industry",
		pharmaSectionContent: `Following clinical and scientific validation, EpilexIA provides a unique ecosystem for the pharmaceutical industry, enabling deep, evidence-based insights on patient populations with rare epilepsies.`,
		pharmaSectionContent2:
			"Pharma access to insights occurs only after clinical-partner validation, ethical approval and formal review processes",
		pharmaSectionSubtitle1: "Key benefits:",
		pharmaSectionSubtitle2: "Notice:",
		pharmaSectionPoints: [
			"Early identification of clinically relevant correlations.",
			"Scientifically grounded patient and phenotype profiling.",
			"Longitudinal observation of therapeutic evolution (once validated).",
			"Hypothesis generation for clinical study design.",
			"Support for translational research and new therapy development.",
		],
		phasesSectionTitle: "Project Phases toward Pharma-grade Availability",
		phasesSectionContent: `EpilexIA progresses through a complete and ethical biomedical development cycle ensuring that any insight is validated before becoming available to industry.`,
		phasesSectionSubtitle: "Project phases:",
		phasesSectionPoints: [
			"Initial implementation: data ingestion, anonymization and model activation.",
			"Preliminary discoveries: early findings with clinical specialists.",
			"Scientific validation: expert neurologist review and clinical partner oversight.",
			"Peer review: publication and external scientific audit.",
			"Global medical communication: dissemination in clinical and scientific forums.",
			"Commercial availability for pharma: regulated, ethical access to validated insights.",
		],
		//Ethics & Privacy
		ethicsSectionTitle: "GDPR, Privacy & Anonymization",
		ethicsSectionContent1: "EpilexIA is built on strict privacy, data security, and regulatory compliance.",
		ethicsSectionSubTitle: "Key principles:",
		ethicsSectionPoints: [
			"Complete anonymization using SHA-256, AES-256, and structured de-identification",
			"Zero personal identifiers stored in analytical systems",
			"Local anonymization executed directly on local server.",
			"Full GDPR compliance: data minimization, transparency, purpose limitation",
			"Secure architecture using GDPR-compliant AWS services",
		],
		ethicsSectionContent2: "Privacy and ethics are foundational pillars of EpilexIA.",
		//Collaborators
		collaboratorsSectionTitle: "Who - Official Collaborators",
		collaboratorsSectionContent:
			"We are proud to announce official collaboration agreements with the institutions listed below. Their participation will be key in guiding the platform’s roadmap and shaping its ongoing evolution.",

		foundationNames: [
			"INCE Foundation Spain ",
			"Dravet Syndrome Foundation Spain",
			"Dravet Syndrome Foundation UK",
			"LGS Foundation USA",
			"Complutense University of Madrid (UCM)",
			"Pennsieve",
		],
		collaboratorsSectionPoints: [
			": Leading epilepsy and neurophysiology center.",
			": Major scientific and family-support organization.",
			": Internationally influential foundation advancing research and education.",
			`: The world’s leading foundation for Lennox-Gastaut syndrome.`,
			": Top European research university with robust neuroscience programs.",
			": The Pennsieve platform is a cloud-based scientific data management platform focused on integrating complex datasets, fostering collaboration, and publishing scientific data according to all FAIR principles of data sharing. The platform includes the Epilepsy.Science Portal and Pennsieve Discover repositories. It supports flexible metadata schemas and a large number of scientific file formats and modalities.",
		],
		//Investors
		investorsSectionTitle: "Investors & Strategic Partners",
		investorsSectionContent1:
			"EpilexIA welcomes visionary investors who want to support a clinically validated AI platform with global potential.",
		investorsSectionSubTitle: "Investors gain the opportunity to back:",
		investorsSectionPoints: [
			"A breakthrough AI ecosystem transforming epilepsy and rare-disease research",
			"A platform co-developed with leading neurologists, universities, and foundations",
			"A scalable, privacy-first technology built for long-term scientific and commercial impact",
		],
		investorsSectionContent2: "Join EpilexIA and help shape the future of global neuroscience innovation.",

		//Mobile app
		mobileSectionTitle: "Free & Opt-in Global Mobile App",
		mobileSectionContent1:
			"The EpilexIA mobile app is a free, optional companion built for global patients and caregivers, designed with strict privacy and anonymity.",
		mobileSectionSubtitle: "Key capabilities:",
		mobileSectionPoints: [
			"Anonymous seizure logging with context, triggers, evolution, and frequency",
			"On-device private data vault with export only when the user consents",
			"Doctor-curated multimedia education including videos, guides, and syndrome-specific content",
			"Generative AI “Chat With Your Data” for easy-to-understand insights",
			"Visit-ready medical reports to support communications with neurologists",
			"Optional emergency helpers",
			"Biometric integration with wearable devices, enabling a holistic view with:",
		],
		mobileSectionPoints2: [
			"Sleep data (deep/REM when available)",
			"Heart rate and HRV",
			"Physical activity, steps, and daily rhythms",
		],
		mobileSectionContent2:
			"These signals combine with seizure logs to produce a longitudinal, holistic view of patient health—fully anonymized and opt-in.",

		mobileSectionDownloadAppTitle: "Download the App",

		//Inside the platform
		insideSectionTitle: "What’s inside the platform",
		insideSectionContent: "EpilexIA offers a comprehensive ecosystem for clinicians and researchers:",
		insideSectionPoints: [
			"Multimodal AI seizure-analysis engine",
			"Graph-based medical record explorer",
			"ML modules for trajectory modeling, clusters, and anomalies",
			"Generative AI for summarization and scientific hypothesis generation",
			"Agentic AI for advanced, context-aware, human-supervised patient analysis",
			"Rare-epilepsy domains (Dravet, West, LGS)",
			"RWE dashboards for trends, evolution, and correlations",
			"Secure anonymization and ingestion pipeline",
			"Collaborative framework with leading clinical and academic institutions",
		],

		contactSectionTitle: "Contact",
		footerContact: "Contact us:",

		//Contact us
		contactUsSectionTitle: "Contact Us",
		formNameLabel: "Name",
		formEmailLabel: "Email",
		formMessageLabel: "Your Message",
		formButton: "Send",
		formButtonSending: "Sending...",
	},
	//ESPAÑOL
	es: {
		menuLink1: "Que es EpilexIA?",
		menuLink2: "Por qué EpilexIA importa",
		menuLink3: "Solución EpilexIa",
		menuLink4: "Dentro de la plataforma",
		menuLink5: "Beneficios para investigadores",
		menuLink6: "Beneficios industria farmacéutica",
		menuLink7: "Fases del proyecto",
		menuLink8: "Aplicación móvil",
		menuLink9: "Colaboradores",
		menuLink10: "Ética y privacidad",
		menuLink11: "Inversores y socios",
		menuLink12: "Contáctanos",
		heroTitle: "EpilexIA",
		heroTitle2: "Inteligencia Artificial Multimodal para Epilepsias Raras.",
		heroDescription:
			"Una plataforma IA multimodal de próxima generación diseñada para analizar registros médicos reales de pacientes con encefalopatías epilépticas complejas, integrando aprendizaje automático, PLN biomédico, visión por computadora y grafos de conocimiento clínico.",
		learnMore: "Aprende más",
		collaborationInquiry: "Consulta de colaboración",
		menu: "Menú",
		platform: "Plataforma",
		clinicalChallenge: "Desafío Clínico",
		heroBtn1: "Quiero saber más",
		heroBtn2: "Contacto para colaboración",

		//platform
		platformSectionTitle: "¿Qué es EpilexIA? ",
		platformSectionContent: `EpilexIA es una plataforma de IA multimodal de última generación diseñada para transformar el análisis, el diagnóstico y la investigación de la epilepsia. Integra visión artificial, PLN, aprendizaje automático, IA generativa, IA agéntica y bases de datos de gráficos para unificar los hallazgos de EEG/RMN, las notas clínicas, los datos biométricos, las descripciones de las crisis y los historiales de tratamiento en una única capa de inteligencia.`,
		platformSectionSubtitle: "EpilexIA is:",
		platformSectionPoints: [
			"A multimodal AI engine capable of processing imaging, text, wearable signals, and structured clinical data.",
			"A graph-powered intelligence network mapping symptoms, biomarkers, genetics, comorbidities, medications, and disease trajectories.",
			"A real-world evidence platform built from deeply anonymized medical histories.",
			"An agentic AI system capable of reasoning over patient records, medical literature, drug evidence, and ML-derived insights.",
			"A human-in-the-loop clinical decision-support ecosystem, where expert neurologists guide, validate, and supervise AI reasoning.",
			"A rare-epilepsy research framework specializing in Dravet, West, and Lennox-Gastaut syndromes.",
			"A privacy-first, GDPR-aligned environment co-developed with leading clinicians and foundations.",
		],

		//clinical challenge
		clinicalSectionTitle: "¿Por qué es importante EpilexIA?",
		clinicalSectionSubtitle: "EpilexIA aborda obstáculos de larga data en la atención y la investigación de la epilepsia:",
		clinicalSectionContent: "EpilexIA aporta claridad, integración y aceleración científica en todo el campo.",
		clinicalSectionPoints: [
			"Las epilepsias raras son trastornos neurológicos graves y complejos que afectan principalmente a los niños.",
			"Información clínica fragmentada en imágenes, narrativas y registros de tratamiento.",
			"Conjuntos de datos estructurados limitados para encefalopatías epilépticas raras.",
			"Incertidumbre diagnóstica causada por presentaciones variables.",
			"Desafíos para predecir la evolución de la enfermedad y la respuesta al tratamiento.",
			"Procesos de investigación manuales, lentos y laboriosos.",
			"Necesidad de una IA confiable, explicable y alineada con los estándares médicos.",
		],

		//solution
		solutionSectionTitle: "¿Cómo funciona EpilexIA?",
		solutionSectionContent: `procesa información clínica multimodal a través de un proceso estructurado e impulsado por IA:`,
		solutionSectionSubTitle1: "Capa ETL de IA generativa (Extracción, transformación y carga)",
		solutionSectionSubTitle2: "Análisis de IA multimodal",
		solutionSectionSubTitle3: "IA agente para razonamiento clínico profundo (humano en el circuito)",
		solutionSectionSubTitle4: "Perspectivas e integración de conocimientos de Real World Evidence(RWE)",
		solutionSectionSubtitleText1: "EpilexIA incorpora un avanzado sistema de IA agente con supervisión médica:",
		solutionSectionSubtitleText2:
			"Este modelo de inteligencia híbrida combina la precisión de la IA con el juicio médico experto.",
		solutionSectionPoints1: [
			"Lee documentos no estructurados (PDF, notas médicas, informes de EEG/MRI).",
			"Extrae conceptos médicos clave, eventos, cronologías y correlaciones.",
			"Produce conjuntos de datos relacionales estandarizados para análisis estructurado.",
			"Crea automáticamente bases de datos de gráficos que capturan relaciones entre síntomas, imágenes, genética, medicamentos y resultados.",
		],
		solutionSectionPoints2: [
			"La visión artificial interpreta EEG, resonancias magnéticas y secuencias de imágenes.",
			"La PNL estructura historias clínicas de formato largo.",
			"El aprendizaje automático y la IA generativa detectan grupos, anomalías, patrones terapéuticos y señales predictivas.",
		],
		solutionSectionPoints3: [
			"Cruza los datos de cada paciente con una base de conocimientos sobre epilepsia seleccionada por expertos.",
			"Revisa las correlaciones con los mecanismos de los medicamentos, los perfiles de efectos secundarios, las pautas de dosificación y la evidencia científica.",
			"Integra información de ML, patrones temporales y tendencias a nivel de población.",
			"Produce vías de razonamiento estructuradas y explicables para respaldar la comprensión clínica.",
			"Los neurólogos expertos permanecen informados, guiando el desarrollo del modelo, validando interpretaciones y garantizando la seguridad clínica.",
		],
		solutionSectionPoints4: [
			"Genera paneles de control de combustible, herramientas de investigación exploratoria y módulos de conocimiento clínico.",
			"Los ciclos de aprendizaje continuo mejoran la precisión a medida que más casos ingresan a la plataforma.",
			"Todos los procesos cumplen con el RGPD y los requisitos éticos.",
		],

		cliniciansSectionTitle: "Beneficios para Clínicos",
		clicniciandSectionContent: [
			"Acceso rápido a información estructurada del paciente.",
			"Mejora en la comprensión longitudinal de la evolución clínica.",
			"Visualización clara de correlaciones relevantes.",
			"Apoyo a la toma de decisiones en casos complejos.",
		],
		reasearchersSectionTitle: "Beneficios para Investigadores",
		researchersSectionContent: [
			"Cohortes dinámicas y filtrables.",
			"Identificación de patrones y biomarcadores potenciales.",
			"Análisis reproducible y trazable.",
			"Descubrimiento científico colaborativo.",
		],
		pharmaSectionTitle: "Beneficios para la Industria Farmacéutica",
		pharmaSectionContent: `Tras las fases de validación clínica y científica, EpilexIA ofrece un ecosistema único para la industria farmacéutica, con potencial para generar insights profundos y basados en evidencia sobre poblaciones de pacientes con epilepsias raras.`,
		pharmaSectionContent2:
			"El acceso a perspectivas para industria farmacéutica solo ocurre después de validación por socios clínicos, aprobación ética y procesos formales de revisión.",
		pharmaSectionSubtitle1: "Beneficios clave:",
		pharmaSectionSubtitle2: "Advertencia:",
		pharmaSectionPoints: [
			"Identificación temprana de correlaciones clínicas relevantes.",
			"Profiler de pacientes y fenotipos complejos con base científica.",
			"Observación longitudinal de evolución terapéutica (una vez validada).",
			"Generación de hipótesis para diseño de estudios clínicos.",
			"Soporte para investigación traslacional y desarrollo de nuevas terapias.",
		],
		phasesSectionTitle: "Fases del Proyecto hacia Comercialización",
		phasesSectionContent: `EpilexIA avanza en un ciclo completo y ético de desarrollo biomédico que garantiza que cualquier conocimiento sea validado antes de estar disponible para la industria.`,
		phasesSectionSubtitle: "Fases del proyecto:",
		phasesSectionPoints: [
			"Implementación inicial: ingestión de historias clínicas, anonimización, puesta en marcha de modelos.",
			"Descubrimientos preliminares: hallazgos iniciales en colaboración con especialistas.",
			"Validación científica: revisión por neurólogos expertos y centros clínicos.",
			"Revisión por pares: publicación y auditoría científica externa.",
			"Comunicación médica global: presentación en foros científicos y clínicos.",
			"Disponibilidad para la industria farmacéutica: acceso regulado y ético a insights validados.",
		],
		//Etica y Privacidad
		ethicsSectionTitle: "Privacidad Y Anonimización",
		ethicsSectionContent1: "EpilexIA se basa en estrictos principios de privacidad, seguridad de datos y cumplimiento normativo.",
		ethicsSectionSubTitle: "Principios clave:",
		ethicsSectionPoints: [
			"Anonimización completa mediante SHA-256, AES-256 y desidentificación estructurada",
			"Cero identificadores personales almacenados en sistemas analíticos",
			"Anonimización local ejecutada directamente por los médicos",
			"Cumplimiento total del RGPD: minimización de datos, transparencia, limitación de la finalidad",
			"Arquitectura segura que utiliza servicios de AWS que cumplen con el RGPD",
		],
		ethicsSectionContent2: "La privacidad y la ética son pilares fundamentales de EpilexIA.",

		//Inversores
		investorsSectionTitle: "Inversores y socios estratégicos",
		investorsSectionContent1:
			"EpilexIA da la bienvenida a inversores visionarios que quieran apoyar una plataforma de IA clínicamente validada con potencial global.",
		investorsSectionSubTitle: "Los inversores tienen la oportunidad de respaldar:",
		investorsSectionPoints: [
			"Un ecosistema de IA innovador que transforma la investigación sobre la epilepsia y las enfermedades raras.",
			"Una plataforma desarrollada conjuntamente con neurólogos líderes, universidades y fundaciones.",
			"Una tecnología escalable que prioriza la privacidad y está diseñada para tener un impacto científico y comercial a largo plazo.",
		],
		investorsSectionContent2: "Únase a EpilexIA y ayude a dar forma al futuro de la innovación en neurociencia global.",

		//Mobile app
		mobileSectionTitle: "Aplicación móvil global gratuita y de suscripción voluntaria",
		mobileSectionContent1:
			"La aplicación móvil EpilexIA es un complemento gratuito y opcional creado para pacientes y cuidadores de todo el mundo, diseñado con estricta privacidad y anonimato.",
		mobileSectionSubtitle: "Capacidades clave:",
		mobileSectionPoints: [
			"Registro anónimo de crisis con contexto, desencadenantes, evolución y frecuencia.",
			"Bóveda de datos privados en el dispositivo con exportación solo cuando el usuario da su consentimiento",
			"Educación multimedia elaborada por médicos que incluye videos, guías y contenido específico sobre cada síndrome.",
			"IA generativa 'Chatea con tus datos' para obtener información fácil de entender",
			"Informes médicos listos para la visita para apoyar las comunicaciones con los neurólogos",
			"Ayudantes de emergencia opcionales",
			"Integración biométrica con dispositivos portátiles, lo que permite una visión holística con:",
		],
		mobileSectionPoints2: [
			"Datos del sueño (profundo/REM cuando estén disponibles)",
			"Frecuencia cardíaca y VFC",
			"Actividad física, pasos y ritmos diarios",
		],
		mobileSectionContent2:
			"Estas señales se combinan con los registros de convulsiones para producir una visión longitudinal y holística de la salud del paciente, totalmente anónima y voluntaria.",
		mobileSectionDownloadAppTitle: "Descarga la Aplicación",
		//Inside the platform
		insideSectionTitle: "¿Qué hay dentro de la plataforma?",
		insideSectionContent: "EpilexIA ofrece un ecosistema integral para médicos e investigadores:",
		insideSectionPoints: [
			"Multimodal AI seizure-analysis engine",
			"Graph-based medical record explorer",
			"ML modules for trajectory modeling, clusters, and anomalies",
			"Generative AI for summarization and scientific hypothesis generation",
			"Agentic AI for advanced, context-aware, human-supervised patient analysis",
			"Rare-epilepsy domains (Dravet, West, LGS)",
			"RWE dashboards for trends, evolution, and correlations",
			"Secure anonymization and ingestion pipeline",
			"Collaborative framework with leading clinical and academic institutions",
		],

		//Collaborators
		collaboratorsSectionTitle: "Quiénes - Colaboradores Oficiales",
		collaboratorsSectionContent:
			"Nos enorgullece anunciar acuerdos oficiales de colaboración con las instituciones que se muestran a continuación. Su participación será clave para orientar la hoja de ruta de la plataforma y contribuir a su evolución continua.",
		foundationNames: [
			"Fundación INCE España",
			"Fundación Síndrome de Dravet España",
			"Fundación del Síndrome de Dravet del Reino Unido",
			"Fundación LGS EE.UU.",
			"Universidad Complutense de Madrid (UCM)",
			"Pennsieve",
		],
		collaboratorsSectionPoints: [
			": Centro líder en epilepsia y neurofisiología.",
			": Importante organización científica y de apoyo a las familias.",
			": Fundación con influencia internacional que promueve la investigación y la educación.",
			`: La fundación líder a nivel mundial para el síndrome de Lennox-Gastaut.`,
			": La principal universidad de investigación europea con sólidos programas de neurociencia.",
			": La plataforma Pennsieve es una plataforma de gestión de datos científicos basada en la nube, centrada en la integración de conjuntos de datos complejos, el fomento de la colaboración y la publicación de datos científicos conforme a los principios FAIR de intercambio de datos. La plataforma incluye el portal Epilepsy.Science y los repositorios Pennsieve Discover. Admite esquemas de metadatos flexibles y una gran variedad de formatos y modalidades de archivos científicos.",
		],

		//Contact us
		contactUsSectionTitle: "Contáctanos",
		formNameLabel: "Nombre",
		formEmailLabel: "Email",
		formMessageLabel: "Tu Mensaje",
		formButton: "Enviar",
		formButtonSending: "Enviando...",

		footerContact: "Contáctanos:",
	},
};
